import React, { useState, useEffect } from "react";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { useSelector } from "react-redux";
import { api1 } from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import { toast } from "react-toastify";

const CareerBookmarkButton = ({ 
  career, 
  size = "md",
  variant = "outline",
  isBookmarked: initialBookmarked = false,
  onBookmarkChange = () => {}
}) => {
  const [isBookmarked, setIsBookmarked] = useState(initialBookmarked);
  const [isLoading, setIsLoading] = useState(false);
  
  const userId = useSelector((state) => state.auth.id);

  useEffect(() => {
    setIsBookmarked(initialBookmarked);
  }, [initialBookmarked, career?.name]);

  const handleBookmarkToggle = async () => {
    if (!career || !userId) {
      toast.error("Please log in to save careers");
      return;
    }

    if (isBookmarked) {
      toast.info("This career is already saved");
      return;
    }

    try {
      setIsLoading(true);
      
      const careerType = career.category || (career.necessarySkills ? "mainstream" : "emerging");
      const careerName = career.name;
      const description = career.description || "";
      
      // Gather all skills from different categories
      const allSkills = [];
      
      // Add common skills
      if (career.commonSkills && Array.isArray(career.commonSkills)) {
        career.commonSkills.forEach(skill => {
          if (typeof skill === 'string') {
            allSkills.push(skill);
          } else if (skill && skill.name) {
            allSkills.push(skill.name);
          }
        });
      }
      
      // Add defining skills
      if (career.definingSkills && Array.isArray(career.definingSkills)) {
        career.definingSkills.forEach(skill => {
          if (typeof skill === 'string') {
            allSkills.push(skill);
          } else if (skill && skill.name) {
            allSkills.push(skill.name);
          }
        });
      }
      
      // Add distinguishing skills
      if (career.distinguishingSkills && Array.isArray(career.distinguishingSkills)) {
        career.distinguishingSkills.forEach(skill => {
          if (typeof skill === 'string') {
            allSkills.push(skill);
          } else if (skill && skill.name) {
            allSkills.push(skill.name);
          }
        });
      }
      
      // Add salary boosting skills
      if (career.salaryBoostingSkills && Array.isArray(career.salaryBoostingSkills)) {
        career.salaryBoostingSkills.forEach(skill => {
          if (typeof skill === 'string') {
            allSkills.push(skill);
          } else if (skill && skill.name) {
            allSkills.push(skill.name);
          }
        });
      }
      
      // Add necessary skills (for mainstream careers)
      if (career.necessarySkills && Array.isArray(career.necessarySkills)) {
        career.necessarySkills.forEach(skill => {
          if (typeof skill === 'string') {
            allSkills.push(skill);
          } else if (skill && skill.name) {
            allSkills.push(skill.name);
          }
        });
      }
      
      // Add tools and technologies (for emerging careers)
      if (career.tools_and_technologies && Array.isArray(career.tools_and_technologies)) {
        career.tools_and_technologies.forEach(skill => {
          if (typeof skill === 'string') {
            allSkills.push(skill);
          } else if (skill && skill.name) {
            allSkills.push(skill.name);
          }
        });
      }
      
      // Remove duplicates and filter out empty values
      const skills = [...new Set(allSkills)].filter(skill => skill && skill.trim() !== '');
      
      const bookmarkData = {
        userId,
        careerName,
        careerType,
        description,
        skills
      };

      // Only save career (can't unsave from this button)
      await api1.post(API_ENDPOINTS.SAVE_CAREER_BOOKMARK, bookmarkData);
      setIsBookmarked(true);
      onBookmarkChange(true, career);
      toast.success("Career saved to your list");
    } catch (error) {
      console.error("Error saving bookmark:", error);
      toast.error("Failed to save career");
    } finally {
      setIsLoading(false);
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case "sm":
        return "w-8 h-8";
      case "lg":
        return "w-12 h-12";
      default:
        return "w-10 h-10";
    }
  };

  const getVariantClasses = () => {
    switch (variant) {
      case "filled":
        return isBookmarked 
          ? "bg-purple-600 text-white hover:bg-purple-700" 
          : "bg-gray-200 text-gray-600 hover:bg-gray-300";
      case "minimal":
        return "text-gray-400 hover:text-purple-600";
      default:
        return isBookmarked
          ? "bg-purple-100 text-purple-600 border-purple-200 hover:bg-purple-200"
          : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50";
    }
  };


  return (
    <button
      onClick={handleBookmarkToggle}
      disabled={isLoading || isBookmarked}
      className={`
        ${getVariantClasses()}
        rounded-lg border flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition-all duration-200
        disabled:opacity-50 disabled:cursor-not-allowed
        ${variant === "minimal" ? "border-none" : "border"}
        ${isBookmarked ? "bg-green-50 border-green-200 text-green-600" : ""}
      `}
      title={isBookmarked ? "Already saved - cannot click" : "Save career"}
    >
      {isLoading ? (
        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-current"></div>
      ) : isBookmarked ? (
        <BookmarkCheck className="w-4 h-4" />
      ) : (
        <Bookmark className="w-4 h-4" />
      )}
      <span>{isBookmarked ? "Saved" : "Save For Review"}</span>
    </button>
  );
};

export default CareerBookmarkButton;
